import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import RocketIcon from "../../components/RocketIcon";
import { useAdminAuth } from "../../contexts/AdminAuthContext";

export default function AdminLoginPage() {
  const { admin, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect when admin is already logged in (or just logged in)
  useEffect(() => {
    if (admin && admin !== false) {
      if (admin.must_change_password) {
        navigate("/admin/change-password", { replace: true });
      } else {
        const next = location.state?.from || "/admin/dashboard";
        navigate(next, { replace: true });
      }
    }
  }, [admin, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const data = await login(email.trim(), password);
      // Navigation handled by useEffect above after admin state updates
      if (data?.must_change_password) {
        navigate("/admin/change-password", { replace: true });
      } else {
        navigate(location.state?.from || "/admin/dashboard", { replace: true });
      }
    } catch (e) {
      setError(e.message || "Giriş başarısız.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-cream flex items-center justify-center p-5">
      <Helmet>
        <title>Admin · Yastık Medya</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="relative w-full max-w-md">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-pink/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-teal/25 blur-3xl" />

        <div className="relative rounded-3xl bg-white border border-mist shadow-card p-8 sm:p-10">
          <div className="flex items-center gap-2.5">
            <RocketIcon size={32} id="login" className="-rotate-[15deg]" />
            <div className="leading-tight">
              <div className="font-display text-lg font-extrabold lowercase">yastık medya</div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-brand-pink-deep font-bold">admin paneli</div>
            </div>
          </div>

          <h1 className="mt-7 font-display text-h1 font-bold text-ink leading-tight">
            Tekrar hoş geldin.
          </h1>
          <p className="mt-2 text-sm text-ink/60">
            Yastık Medya yönetim paneline giriş yap.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">E-posta</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute start-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email"
                  className="w-full rounded-xl border-2 border-mist bg-cream/60 ps-10 pe-4 py-3 text-sm focus:border-brand-pink focus:bg-white outline-none transition-colors"
                  placeholder="e-posta adresi"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Şifre</label>
              <div className="mt-1.5 relative">
                <Lock className="absolute start-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  className="w-full rounded-xl border-2 border-mist bg-cream/60 ps-10 pe-4 py-3 text-sm focus:border-brand-pink focus:bg-white outline-none transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error ? (
              <div data-testid="admin-login-error" className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-cream px-7 py-3.5 text-sm font-semibold hover:bg-brand-pink-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Giriş yapılıyor…" : "Giriş yap"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
